import React, { useState, useEffect } from "react";
import { updateStock, bulkUpdateStock } from "../../services/inventory.service";
import { exportToExcel, exportToCSV } from "../../utils/excel";
import "./inventory-stock-table.css";

const InventoryStockTable = ({ items = [], orgId, location }) => {
  const [inventoryData, setInventoryData] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initialData = {};
    items.forEach((item) => {
      initialData[item.item_id] = {
        item_id: item.item_id,
        opening: 0,
        receivers: 0,
        sold: 0,
        nc: 0,
        damage: 0,
        sent: 0,
        closing: 0,
        missing: 0,
      };
    });
    setInventoryData(initialData);
  }, [items]);

  const handleInputChange = async (itemId, field, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);

    const updatedData = {
      ...inventoryData,
      [itemId]: {
        ...inventoryData[itemId],
        [field]: numValue,
      },
    };

    setInventoryData(updatedData);

    try {
      setLoading(true);
      await updateStock(itemId, updatedData[itemId], { orgId, location });
    } catch (error) {
      console.error("Failed to update stock:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    newSelected.has(itemId)
      ? newSelected.delete(itemId)
      : newSelected.add(itemId);
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    selectedItems.size === items.length
      ? setSelectedItems(new Set())
      : setSelectedItems(new Set(items.map((i) => i.item_id)));
  };

  const handleBulkUpdate = async () => {
    const selectedData = Array.from(selectedItems).map((itemId) => ({
      ...inventoryData[itemId],
      item_id: itemId,
    }));

    try {
      setLoading(true);
      await bulkUpdateStock(selectedData, { orgId, location });
      setSelectedItems(new Set());
    } catch (error) {
      console.error("Bulk update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const totals = Object.values(inventoryData).reduce(
    (acc, item) => {
      acc.opening += item.opening;
      acc.receivers += item.receivers;
      acc.sold += item.sold;
      acc.nc += item.nc;
      acc.damage += item.damage;
      acc.sent += item.sent;
      acc.closing += item.closing;
      acc.missing += item.missing;
      return acc;
    },
    {
      opening: 0,
      receivers: 0,
      sold: 0,
      nc: 0,
      damage: 0,
      sent: 0,
      closing: 0,
      missing: 0,
    }
  );

  const handleExportExcel = () => {
    exportToExcel(
      items
        .map((i) => ({ "Item Name": i.item_name, ...inventoryData[i.item_id] }))
        .concat([{ "Item Name": "Total", ...totals }]),
      "inventory_stock.xlsx"
    );
  };

  const handleExportCSV = () => {
    exportToCSV(
      items
        .map((i) => ({ "Item Name": i.item_name, ...inventoryData[i.item_id] }))
        .concat([{ "Item Name": "Total", ...totals }]),
      "inventory_stock.csv"
    );
  };

  return (
    <div className="inventory-stock-table">
      {/* BUTTON BAR */}
      <div className="table-controls">
        <button
          onClick={handleBulkUpdate}
          disabled={loading || selectedItems.size === 0}
        >
          Bulk Update ({selectedItems.size})
        </button>

        <div className="export-buttons">
          <button onClick={handleExportExcel} disabled={loading}>
            Export to Excel
          </button>
          <button onClick={handleExportCSV} disabled={loading}>
            Export to CSV
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="inventory-table">
          <thead className="sticky-header">
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedItems.size === items.length && items.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th>S.No</th>
              <th>Item Name</th>
              <th>Opening</th>
              <th>Receivers</th>
              <th>Sold</th>
              <th>NC</th>
              <th>Damage</th>
              <th>Sent</th>
              <th>Closing</th>
              <th>Missing</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item, index) => {
              const d = inventoryData[item.item_id];
              return (
                <tr key={item.item_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.item_id)}
                      onChange={() => handleSelectItem(item.item_id)}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td>{item.item_name}</td>

                  {[
                    "opening",
                    "receivers",
                    "sold",
                    "nc",
                    "damage",
                    "sent",
                    "closing",
                    "missing",
                  ].map((key) => (
                    <td
                      key={key}
                      className={
                        key === "missing" && d && d[key] > 0 ? "highlight-red" : ""
                      }
                    >
                      <input
                        type="number"
                        min="0"
                        value={d ? d[key] : 0}
                        onChange={(e) =>
                          handleInputChange(item.item_id, key, e.target.value)
                        }
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>

          {/* ✅ FIXED FOOTER ALIGNMENT */}
          <tfoot className="sticky-footer">
            <tr>
              <td colSpan="3">Total</td>
              <td>{totals.opening}</td>
              <td>{totals.receivers}</td>
              <td>{totals.sold}</td>
              <td>{totals.nc}</td>
              <td>{totals.damage}</td>
              <td>{totals.sent}</td>
              <td>{totals.closing}</td>
              <td>{totals.missing}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default InventoryStockTable;
